import { ApiError, ChannelBadge, Empty, IconButton, Loading, Select, Tip, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Bot,
  Briefcase,
  CircleDot,
  Phone,
  TimerReset,
  UserCheck,
  UserRound,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { NewCaseDialog, UserSelect } from '../crm/forms';
import { api } from '../lib/api';
import { safeHref } from '../lib/links';
import { useAuth } from '../lib/auth';
import { useTeamName, useTeams } from '../pages/Teams';
import { Composer } from './Composer';
import { Stars } from './List';
import { MessageList } from './MessageList';
import type { ConversationDetail, PresenceEntry, ThreadItem } from './types';

function SlaLine({ d }: { d: ConversationDetail['conversation'] }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (d.firstResponseAt)
    return (
      <span className="sla-line ok">{t('inbox.sla.met', { rel: fmt.relative(d.firstResponseAt) })}</span>
    );
  if (d.slaBreachedAt || (d.firstResponseDueAt && new Date(d.firstResponseDueAt) < new Date())) {
    return (
      <span className="sla-line late" data-testid="sla-line">
        <TimerReset size={13} />
        {t('inbox.sla.overdue', { time: fmt.dateTimeZoned(d.firstResponseDueAt!) })}
      </span>
    );
  }
  if (d.firstResponseDueAt)
    return (
      <span className="sla-line" data-testid="sla-line">
        <TimerReset size={13} />
        {t('inbox.sla.dueIn', {
          time: fmt.timeZoned(d.firstResponseDueAt),
          rel: fmt.relative(d.firstResponseDueAt),
        })}
      </span>
    );
  return null;
}

export function Thread({
  id,
  onBack,
  presence,
  sendPresence,
  onToggleCustomer,
}: {
  id: string;
  onBack: () => void;
  /** On mid-size screens the customer panel replaces the list; this toggles it. */
  onToggleCustomer?: () => void;
  presence: PresenceEntry[];
  sendPresence: (state: 'viewing' | 'typing' | 'left', conversationId?: string) => void;
}) {
  const { t } = useTranslation();
  const { session, can } = useAuth();
  const teams = useTeams();
  const teamName = useTeamName();
  const qc = useQueryClient();
  const [caseOpen, setCaseOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => api<ConversationDetail>(`/v1/conversations/${id}`),
  });
  const messages = useQuery({
    queryKey: ['messages', id],
    queryFn: () => api<{ items: ThreadItem[] }>(`/v1/conversations/${id}/messages`),
  });
  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api(`/v1/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', id] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Presence: tell colleagues we're looking at this conversation (heartbeat), and when we leave.
  useEffect(() => {
    sendPresence('viewing', id);
    const i = setInterval(() => sendPresence('viewing', id), 15_000);
    return () => {
      clearInterval(i);
      sendPresence('left', id);
    };
  }, [id, sendPresence]);

  if (detail.isLoading) return <Loading />;
  if (!detail.data) return <Empty text={t('inbox.selectPrompt')} />;
  const d = detail.data;
  const others = presence.filter(
    (p) => p.userId !== session?.user.id && p.state !== 'left' && Date.now() - p.at < 35_000,
  );
  const typingNow = others.find((p) => p.state === 'typing' && Date.now() - p.at < 6_000);

  return (
    <>
      <div className="card-head thread-head">
        <button
          className="icon-btn back-btn"
          onClick={onBack}
          aria-label={t('inbox.back')}
          title={t('inbox.back')}
        >
          <ArrowLeft size={16} className="icon-dir" />
        </button>
        <div className="thread-title">
          <h2 dir="auto">
            <Link to={`/people/${d.person.id}`} title={t('inbox.openProfile')}>
              {d.person.displayName}
            </Link>
          </h2>
          <div className="thread-sub">
            <ChannelBadge channel={d.conversation.channel} />
            <SlaLine d={d.conversation} />
            {others.length > 0 && (
              <span className="presence" data-testid="presence">
                <span className="pill-dot live" />
                {typingNow
                  ? t('inbox.presence.typing', { name: typingNow.name })
                  : t('inbox.presence.viewing', { name: others[0]!.name })}
              </span>
            )}
          </div>
        </div>
        <div className="action-bar thread-actions">
          {d.conversation.assigneeUserId !== session?.user.id && (
            <IconButton
              icon={UserCheck}
              label={t('inbox.assignToMe')}
              onClick={() => update.mutate({ assigneeUserId: session?.user.id })}
              data-testid="assign-me"
            />
          )}
          {d.canCall && can('conversations:reply') && (
            <CallButton personId={d.person.id} conversationId={id} />
          )}
          {can('cases:write') && (
            <IconButton icon={Briefcase} label={t('inbox.createCase')} onClick={() => setCaseOpen(true)} />
          )}
          {onToggleCustomer && (
            <IconButton
              icon={UserRound}
              label={t('inbox.customer')}
              onClick={onToggleCustomer}
              side="end"
              data-testid="customer-toggle"
            />
          )}
        </div>
      </div>
      <div className="thread-meta">
        <Tip label={t('inbox.assignee')} className="meta-field">
          <UserRound size={14} aria-hidden="true" className="faint" />
          <UserSelect
            id="conv-assignee"
            value={d.conversation.assigneeUserId ?? ''}
            onChange={(v) => update.mutate({ assigneeUserId: v || null })}
            triggerClassName="select-pill"
            aria-label={t('inbox.assignee')}
          />
        </Tip>
        {can('conversations:assign') ? (
          <Tip label={t('inbox.team')} className="meta-field">
            <Users size={14} aria-hidden="true" className="faint" />
            <Select
              value={d.conversation.team?.id ?? ''}
              onChange={(v) => update.mutate({ teamId: v || null })}
              aria-label={t('inbox.team')}
              data-testid="conv-team"
              triggerClassName="select-pill"
              options={[
                { value: '', label: t('inbox.noTeam') },
                ...(teams.data?.items ?? []).map((x) => ({ value: x.id, label: teamName(x) })),
              ]}
            />
          </Tip>
        ) : (
          d.conversation.team && (
            <span className="badge badge-neutral" dir="auto">
              {teamName(d.conversation.team)}
            </span>
          )
        )}
        <Tip label={t('inbox.statusLabel')} className={`meta-field status-${d.conversation.status}`}>
          <CircleDot size={14} aria-hidden="true" className="status-dot" />
          <Select
            value={d.conversation.status}
            onChange={(v) => update.mutate({ status: v })}
            aria-label={t('inbox.statusLabel')}
            data-testid="conv-status"
            searchable={false}
            triggerClassName="select-pill"
            options={(['open', 'pending', 'snoozed', 'closed'] as const).map((s) => ({
              value: s,
              label: t(`inbox.status.${s}`),
            }))}
          />
        </Tip>
        {d.conversation.requiredSkill && (
          <Tip label={t('inbox.requiredSkill')}>
            <span className="chip chip-skill">
              <span className="val">{d.conversation.requiredSkill}</span>
            </span>
          </Tip>
        )}
      </div>
      <AssistantBar conversationId={id} d={d.conversation} />
      <EngagementContext d={d.conversation} />
      {messages.isLoading ? (
        <Loading />
      ) : (
        <MessageList items={messages.data?.items ?? []} customerName={d.person.displayName} />
      )}
      <Composer detail={d} onTyping={() => sendPresence('typing', id)} />
      {caseOpen && (
        <NewCaseDialog open onClose={() => setCaseOpen(false)} personId={d.person.id} conversationId={id} />
      )}
    </>
  );
}

/** Who is answering: the AI assistant (with "Take over"), or a person after it handed over (with "Hand back"). */
function AssistantBar({
  conversationId,
  d,
}: {
  conversationId: string;
  d: ConversationDetail['conversation'];
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (active: boolean) =>
      api(`/v1/conversations/${conversationId}/assistant`, {
        method: 'POST',
        body: JSON.stringify({ active }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
  if (!d.assistantActive && !d.assistantHandedOffAt) return null;
  return (
    <div
      className={`assistant-bar${d.assistantActive ? ' is-on' : ''}`}
      role="status"
      data-testid="assistant-bar"
    >
      <Bot size={15} aria-hidden="true" />
      <span className="grow">{d.assistantActive ? t('assistant.handling') : t('assistant.handedOver')}</span>
      {can('conversations:reply') && (
        <button
          className="btn btn-sm"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(!d.assistantActive)}
          data-testid={d.assistantActive ? 'take-over' : 'hand-back'}
        >
          {d.assistantActive ? t('assistant.takeOver') : t('assistant.handBack')}
        </button>
      )}
    </div>
  );
}

/** What a comment or review is about: the post (with a link) or the stars, above the thread. */
function EngagementContext({ d }: { d: ConversationDetail['conversation'] }) {
  const { t } = useTranslation();
  if (d.kind !== 'comment' && d.kind !== 'review') return null;
  const c = d.context ?? {};
  return (
    <div className="engagement-context" data-testid="engagement-context">
      {d.kind === 'review' ? (
        <>
          <span className="faint">
            {t(`inbox.context.review_${c.source === 'facebook' ? 'facebook' : 'google'}`)}
          </span>
          {c.rating ? <Stars n={c.rating} /> : null}
        </>
      ) : (
        <>
          <span className="faint">{t('inbox.context.comment')}</span>
          <span className="grow ellipsis" dir="auto">
            {c.postText ? `“${c.postText}”` : t('inbox.context.aPost')}
          </span>
          {safeHref(c.postUrl) && (
            <a href={safeHref(c.postUrl)!} target="_blank" rel="noreferrer noopener">
              {t('inbox.context.open')}
            </a>
          )}
        </>
      )}
    </div>
  );
}

/** "Call with bot": the LY Voice bot calls the customer; the call comes back here when it ends. */
export function CallButton({ personId, conversationId }: { personId: string; conversationId?: string }) {
  const { t } = useTranslation();
  const call = useMutation({
    mutationFn: () =>
      api<{ callId: string }>('/v1/calls', {
        method: 'POST',
        body: JSON.stringify({ personId, conversationId }),
      }),
  });
  const noBot = call.error instanceof ApiError && JSON.stringify(call.error.details ?? '').includes('no_bot');
  return (
    <span className="call-btn">
      <IconButton
        icon={Phone}
        label={t('inbox.call.callNow')}
        onClick={() => call.mutate()}
        disabled={call.isPending || call.isSuccess}
        tone="brand"
        data-testid="call-customer"
      />
      {call.isSuccess && (
        <Tip label={t('inbox.call.calling')} side="end">
          <span className="badge badge-neutral" role="status" data-testid="calling">
            {t('inbox.call.callingShort')}
          </span>
        </Tip>
      )}
      {call.isError && (
        <Tip label={noBot ? t('inbox.call.noBot') : t('inbox.call.callFailed')} side="end">
          <span className="badge badge-danger" role="alert">
            {t('inbox.call.failedShort')}
          </span>
        </Tip>
      )}
    </span>
  );
}
